import React from "react";
import { Clock, DoorOpen, Info, CalendarX2 } from "lucide-react";
import { houseRules, goodToKnow, cancellationLines, fmtTime } from "@/lib/propertyRules";

const Group = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid}>
    <p className="flex items-center gap-2 text-sm font-heading font-bold text-ink">
      <Icon className="w-4 h-4 text-brand" aria-hidden="true" /> {title}
    </p>
    <div className="mt-2 space-y-1.5 text-sm text-ink-soft">{children}</div>
  </div>
);

const Item = ({ text, note }) => (
  <p>
    {text}
    {note ? <span className="text-ink-faint"> — {note}</span> : null}
  </p>
);

// Property rules exactly as the host recorded them. Unset rules render nothing.
export const PropertyRules = ({ rules, testid = "property-rules", title = "Property rules" }) => {
  if (!rules) return null;
  const house = houseRules(rules);
  const know = goodToKnow(rules);
  const cancel = cancellationLines(rules.cancellation_terms);
  const houseText = (rules.house_rules_text || "").trim();
  const times = [
    rules.check_in_from && `Check-in from ${fmtTime(rules.check_in_from)}${rules.check_in_until ? ` until ${fmtTime(rules.check_in_until)}` : ""}`,
    rules.check_out_by && `Check-out by ${fmtTime(rules.check_out_by)}`,
  ].filter(Boolean);
  const childText = (rules.child_ages?.text || "").trim();

  if (!times.length && !house.length && !know.length && !cancel.length && !houseText) return null;

  return (
    <section className="mt-10 pt-8 border-t border-black/5" data-testid={testid}>
      <h2 className="font-heading font-extrabold text-xl tracking-tight text-ink">{title}</h2>
      <div className="mt-5 grid gap-6 sm:grid-cols-2">
        {times.length > 0 && (
          <Group icon={Clock} title="Check-in & check-out" testid={`${testid}-times`}>
            {times.map((t) => <p key={t}>{t}</p>)}
          </Group>
        )}

        {(house.length > 0 || houseText || childText) && (
          <Group icon={DoorOpen} title="House rules" testid={`${testid}-house`}>
            {house.map((r) => <Item key={r.key} text={r.text} note={r.note} />)}
            {childText && <p>{childText}</p>}
            {houseText && <p className="whitespace-pre-line pt-1">{houseText}</p>}
          </Group>
        )}

        {know.length > 0 && (
          <Group icon={Info} title="Good to know" testid={`${testid}-know`}>
            {know.map((r) => <Item key={r.key} text={r.text} note={r.note} />)}
          </Group>
        )}

        {cancel.length > 0 && (
          <Group icon={CalendarX2} title="Cancellation" testid={`${testid}-cancellation`}>
            {cancel.map((l) => <p key={l}>{l}</p>)}
          </Group>
        )}
      </div>
    </section>
  );
};

export default PropertyRules;
